import Navbar from 'pagecomponents/newcomponents/Navbar';
import Footer from 'pagecomponents/newcomponents/Footer';
import Link from 'next/link';
import Head from 'next/head';
import { FREE_TRIAL_APPLICATION_CREDITS } from '@src/config/freeTrial';
import { ELITE_APPLICATION_CREDITS } from '@src/config/planCredits';
import ContentReviewNote from '@/components/ContentReviewNote';
import SeoCtaLink from '@/components/SeoCtaLink';
import styles from '../../styles/auto-apply.module.css';

const title = 'AI Auto Apply: Automatically Apply for Jobs | LifeShack';
const description =
  `Use LifeShack's AI auto-apply agent to automatically apply for matching jobs and submit supported applications. Start with ${FREE_TRIAL_APPLICATION_CREDITS} free credits—no card required.`;
const canonicalUrl = 'https://www.lifeshack.com/ai-auto-apply/';
const signupHref =
  '/signup/?utm_source=seo&utm_medium=organic&utm_campaign=auto_apply&utm_content=ai-auto-apply';
const image =
  'https://production-appsyncstack-companyimages307b450c-17ekd4vq0gwy6.s3.us-west-2.amazonaws.com/lifeshack_opengraph.png';

const faqItems = [
  {
    question: 'Can AI apply for jobs automatically?',
    answer:
      'Yes. An AI job application tool can use your resume, saved answers, and search preferences to find matching roles and submit supported applications. You should keep your profile accurate and review your application activity regularly.',
  },
  {
    question: "How does LifeShack's AI apply to jobs for me?",
    answer:
      'LifeShack searches for roles that fit your job criteria, uses the information in your profile to prepare supported application fields and role-specific resume or cover-letter drafts, and records submitted applications in your dashboard.',
  },
  {
    question: 'What information does LifeShack use?',
    answer:
      'LifeShack uses the resume, job preferences, work history, skills, and reusable application answers you provide. Keeping this information current helps the system find and complete more relevant applications.',
  },
  {
    question: 'What job platforms does LifeShack support?',
    answer:
      'LifeShack works across multiple job-board and applicant-tracking workflows. Employers can customize their application portals, so some applications may be unsupported or require additional candidate information.',
  },
  {
    question: 'What control do I have over automatic applications?',
    answer:
      'You can update your search preferences and resume, pause Auto Apply, track submitted applications, and block companies. You cannot review or approve each application before submission, so check your profile and company blocklist before enabling Auto Apply. Pausing does not withdraw applications already submitted.',
  },
  {
    question: 'How many applications can LifeShack submit each month?',
    answer: `Application credits vary by plan, up to ${ELITE_APPLICATION_CREDITS} per month on the current highest-volume plan. Completed volume depends on matching jobs, supported application flows, and the information available in your profile.`,
  },
  {
    question: 'Can I still apply for jobs manually?',
    answer:
      'Yes. Auto Apply can handle repetitive application work while you apply manually to high-priority roles that need extra research, networking, or personalization.',
  },
  {
    question: 'Is auto-applying with AI safe?',
    answer:
      'Use application automation carefully. Keep your profile current, review where applications are going, and use pause or company-blocking controls when needed. LifeShack does not guarantee interviews or job offers.',
  },
  {
    question: 'Is there a free AI tool that applies for jobs?',
    answer: `Yes. LifeShack starts you with ${FREE_TRIAL_APPLICATION_CREDITS} free application credits, with no credit card required. Use them to judge whether the AI auto-apply workflow fits your preferences before choosing a paid plan.`,
  },
];

export default function Auto() {
    const faqJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      })),
    };

    return (
        <div className={styles.page}>
            <Head>
              <title>{title}</title>
              <link rel="canonical" href={canonicalUrl} />
              <meta name="description" content={description} />
              <meta property="og:title" content={title} />
              <meta property="og:description" content={description} />
              <meta property="og:url" content={canonicalUrl} />
              <meta property="og:image" content={image} />
              <meta property="og:type" content="website" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content={title} />
              <meta name="twitter:description" content={description} />
              <meta name="twitter:image" content={image} />   
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
              />
            </Head>
            <Navbar reserveDesktopSpace />
            <main>
              <HeroSection />
              <ContentReviewNote />
              <About />
              <HowItWorksSection />
              <CTASection />
              <ProductResearchLinks />
              <JobSearchFAQ />
            </main>
            <Footer />
        </div>
    )
}

// This route contains public explanatory content, not the authenticated Auto
// Apply product. Its own SSR stylesheet covers every reachable utility class.
Auto.isPublicSeoPage = true;
// The dashboard illustration is below the explanatory sections, not the hero.
Auto.preloadSeoHero = false;

function About() {
  return (
    <section
      id="application-example"
      aria-labelledby="application-example-heading"
      style={{ scrollMarginTop: '6rem' }}
      className="mx-auto max-w-6xl px-6 py-16"
    >
      <div className="mx-auto max-w-3xl text-center">
        <h2 id="application-example-heading" className="text-3xl font-semibold text-gray-900 md:text-4xl">
          From your preferences to a submitted application.
        </h2>
        <p className="mt-5 text-lg leading-8 text-gray-700">
          What does it mean to automatically apply for jobs? Here is how your
          saved information guides a supported LifeShack application.
        </p>
      </div>
      <div className="mx-auto mt-10 max-w-5xl rounded-2xl border border-gray-200 bg-slate-50 p-6">
        <p className="text-sm font-semibold text-indigo-700">
          Illustrative example—not a real application or product screenshot.
        </p>
        <p className="mt-3 leading-7 text-gray-700">
          Imagine you are looking for customer success roles that are remote in
          the United States. You save those preferences, add your work history
          and reusable answers, and put your current employer on your company
          blocklist.
        </p>
      </div>
      <ol
        aria-label="Example automatic application workflow"
        className="mx-auto mt-6 grid max-w-5xl gap-6 md:grid-cols-3"
      >
        {[
          {
            title: 'Find a matching role',
            detail: 'LifeShack searches available listings using your saved job criteria and company blocklist. A job evaluated or placed in your queue is not yet a submitted application.',
          },
          {
            title: 'Prepare and submit',
            detail: 'For a supported application, LifeShack uses your profile and reusable answers to complete the workflow. Resume and cover-letter tailoring follows your settings. There is no per-application approval step.',
          },
          {
            title: 'Check the recorded result',
            detail: 'Review submitted applications in your dashboard. Employer-specific questions or portal changes can prevent completion or require more information; a match or a draft alone does not mean an application was submitted.',
          },
        ].map(({ title: stepTitle, detail }, index) => (
          <li key={stepTitle} className="rounded-2xl border border-indigo-200 bg-indigo-50 p-6 text-left">
            <p className="text-sm font-bold text-indigo-700">Step {index + 1}</p>
            <h3 className="mt-3 text-xl font-semibold text-gray-900">{stepTitle}</h3>
            <p className="mt-3 leading-7 text-gray-700">{detail}</p>
          </li>
        ))}
      </ol>
      <aside className="mx-auto mt-6 max-w-5xl rounded-2xl border border-gray-200 bg-white p-6">
        <h3 className="text-xl font-semibold text-gray-900">Check your controls before enabling Auto Apply.</h3>
        <p className="mt-3 leading-7 text-gray-700">
          You cannot review or approve each application before submission.
          Check your profile, preferences, and company blocklist first. You can
          pause Auto Apply and update your search from the dashboard, but pausing
          does not withdraw applications already submitted.
        </p>
        <p className="mt-3 leading-7 text-gray-700">
          A submitted application is not an interview or an offer. LifeShack
          does not guarantee either outcome. If you need to approve every
          application yourself, keep those applications manual.
        </p>
        <div className="mt-4 flex flex-wrap gap-4">
          <a href="/help/articles/can-i-review-applications/" className="font-semibold text-indigo-700 underline">
            Application review controls
          </a>
          <a href="/help/articles/can-i-pause-the-service/" className="font-semibold text-indigo-700 underline">
            How to pause Auto Apply
          </a>
        </div>
      </aside>
    </section>
  );
}

const JobSearchFAQ = () => {
  return (
    <section
      className="faq-section mx-auto max-w-5xl px-6 py-20"
      style={{ color: '#333333' }}
    >
      <div className="text-center">
        <p className="text-sm font-semibold uppercase tracking-wider text-indigo-700">
          Frequently asked questions
        </p>
        <h2 className="mt-3 text-3xl font-semibold text-gray-900 md:text-4xl">
          What to know before using AI to apply for jobs.
        </h2>
      </div>
      <div className="mt-12 grid gap-8 md:grid-cols-2">
        {faqItems.map(({ question, answer }) => (
          <article className="faq-item" key={question}>
            <h3 className="text-xl font-semibold text-gray-900">{question}</h3>
            <p className="mt-3 leading-7 text-gray-600">{answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
};

function HowItWorksSection({
  imageUrl = "/widget.webp",
  imageAlt = "Example LifeShack dashboard showing queued jobs, submitted applications, jobs evaluated, and estimated time saved"
}) {
  const steps = [
    {
      title: "Create one application profile",
      body:
        `Upload your current resume and add your work history, skills, preferences, and reusable application answers.`
    },
    {
      title: "Set the roles you want",
      body:
        `Choose target roles, locations, and other job criteria. LifeShack uses those preferences when searching available listings.`
    },
    {
      title: "LifeShack checks each match",
      body:
        `The system compares available roles with your criteria and uses the information in your profile for supported application workflows.`
    },
    {
      title: "Supported applications are submitted",
      body:
        `Portal support can vary when an employer customizes its application or asks for information that only the candidate can provide.`
    },
    {
      title: "Track and refine the workflow",
      body:
        `Review submitted applications in your dashboard, update your preferences, pause the search, or block companies as your priorities change.`
    }
  ];

  return (
    <section className="w-full bg-slate-50" id="how-it-works">
      <div className="mx-auto grid max-w-7xl items-stretch gap-10 px-6 py-16 lg:grid-cols-2 lg:gap-14">
        {/* Image */}
        <figure className="order-2 lg:order-1 flex flex-col justify-center">
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <img
              src={imageUrl}
              alt={imageAlt}
              className="h-auto w-full object-contain"
              loading="lazy"
              width="1718"
              height="756"
            />
          </div>
          <figcaption className="mt-3 text-sm leading-6 text-gray-600">
            Example dashboard: review queued jobs, submitted applications, and
            application activity from one place. The figures shown illustrate
            a typical user and are not a guaranteed result.
          </figcaption>
        </figure>

        {/* Text content */}
        <div className="order-1 lg:order-2 flex flex-col justify-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-indigo-700">
            How it works
          </p>
          <h2 className="mt-3 text-4xl font-semibold leading-tight tracking-tight text-gray-900 md:text-5xl">
            How to automatically apply for jobs with AI.
          </h2>
          <p className="mt-5 max-w-prose text-lg text-gray-600">
            LifeShack handles the repetitive steps in supported applications.
            You provide the source information and decide which roles and
            companies fit your search.
          </p>

          <hr className="mt-6 w-full border-t-2 border-gray-300" />

          <ol className="mt-6 space-y-5">
            {steps.map((s, idx) => (
              <li
                key={s.title}
                className="flex items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
              >
                <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-800 text-sm font-semibold text-white">
                  {idx + 1}
                </div>
                <div>
                  <p className="text-base font-semibold text-gray-900">
                    {s.title}
                  </p>
                  <p className="mt-1 text-gray-600">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}



const HeroSection = () => {
    return (
      <section className="text-white py-20 px-6" style={{backgroundColor: 'rgb(104, 75, 237)'}}>
        <div className="max-w-7xl mx-auto flex flex-col gap-10 md:flex-row md:items-center md:justify-between">
          {/* Text Content */}
          <div className="max-w-2xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-purple-100">
              AI auto-apply agent
            </p>
            <h1 className="text-4xl md:text-5xl mb-4">
              Use AI to Apply for Jobs Automatically
            </h1>
            <p className="text-lg leading-8 md:text-xl">
              Yes—LifeShack can automatically apply to supported jobs for you.
              Add your resume and preferences once, and LifeShack finds matching
              roles, prepares tailored resumes and cover letters, submits
              applications, and tracks them in one dashboard.
            </p>
            <p className="mt-4 font-semibold text-purple-50">
              Start with {FREE_TRIAL_APPLICATION_CREDITS} free application
              credits. No credit card required.
            </p>
          </div>
      
            {/* CTA Button */}
            <div className="flex w-full max-w-sm shrink-0 flex-col items-start gap-4 md:w-96 md:items-center">
              <SeoCtaLink
                href={signupHref}
                pageType="auto_apply"
                placement="hero"
                className="inline-block w-full rounded-lg bg-white px-8 py-5 text-center text-lg font-bold text-purple-700 shadow-lg transition hover:bg-gray-50 hover:shadow-xl"
              >
                Start with free application credits
              </SeoCtaLink>
              <a className="w-full font-semibold text-white underline md:text-center" href="#how-it-works">
                See how it works
              </a>
            </div>
        </div>
      </section>
    );
};

const CTASection = () => {
  return (
    <section className="text-white py-20 px-6" style={{backgroundColor: 'rgb(104, 75, 237)'}}>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between">
        {/* Text Content */}
        <div className="max-w-2xl">
      <h2 className="text-4xl md:text-5xl mb-4">
      Start with free application credits.
      </h2>
      <p className="text-lg md:text-xl">
        Try {FREE_TRIAL_APPLICATION_CREDITS} supported applications with no
        credit card required, then decide whether the workflow fits your job
        search.
      </p>
        </div>
    
          {/* CTA Button */}
          
        <SeoCtaLink
          href={signupHref}
          pageType="auto_apply"
          placement="bottom_cta"
          className="mt-10 inline-block rounded-lg bg-white px-8 py-5 text-lg font-bold text-purple-700 shadow-lg transition hover:bg-gray-50 hover:shadow-xl md:mt-0"
        >
          Start with free application credits
        </SeoCtaLink>
      </div>
    </section>
  );
};

function ProductResearchLinks() {
  return (
    <section className="bg-slate-50 px-6 py-14">
      <div className="mx-auto max-w-4xl text-center">
        <h2 className="text-3xl font-bold text-gray-900">
          Compare auto-apply workflows before choosing.
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          See how cloud agents differ from trackers, autofill extensions, and
          browser automation—and compare LifeShack with seventeen alternatives.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          <Link href="/best-ai-auto-apply-tools/">
            <a className="font-semibold text-indigo-700">
              Best AI auto-apply tools
            </a>
          </Link>
          <Link href="/compare/">
            <a className="font-semibold text-indigo-700">
              Compare LifeShack
            </a>
          </Link>
        </div>
      </div>
    </section>
  );
}
